/**
 * 用語集。作り直しの用語（`listTerms`）の全件を出し、名前・id の語と、種別・値のタイプの切替で
 * 絞り込む。
 *
 * 絞り込みをクライアント側でやるのは、`listTerms` が**絞り込みの入力を持たない**ため
 * である（契約の理由は `spec-graph-element-terms.ts`）。取得済みの配列を絞り、文字を 1 つ打つ
 * たびにサーバー往復を増やさない。
 *
 * 表の列は 名前 ／ 種別 ／ 値のタイプ（Dhik 2026-09-29「用語集の一覧 view でタイプなどのコアな
 * 情報はいくつか出してほしい。タイプについてはフィルターとかつけないと語を探せない」）。種別と
 * 値のタイプは語の形を決める核で、探すときに「プロパティで真偽のもの」のように束ねて引くため、
 * 列に出して切替でも絞れるようにする。属するクラス・指している要素は用語詳細で読む。切替の
 * 選択肢は取得した行に現れる値から組む（値の一覧を画面に写さない）。語の絞り込みと切替は AND で効く。
 *
 * **未設定を隠さない。** 種別・値のタイプが未設定の用語は、セルに「未設定」と書き、切替でも
 * 「未設定」で束ねて引ける（埋めるのは人である）。種別がプロパティでない用語は値のタイプを
 * 書けないので、セルを空ける（`term-field-applicability.ts`）。
 *
 * `?pj=` を渡すと、その案件を適用した後の用語集を出す。探す画面なので左右に割らず、名前の頭に
 * 追加 ／ 削除 ／ 変更の札を置き、案件で足した用語と消した用語を一覧の上に並べる（案件モードの
 * 原則）。消した用語は案件を適用した後の姿に居ないので、読取が別に返す（`removed`）。詳細への
 * リンクは案件番号を引き継ぐ — 落とすと、案件を適用した後の姿を見に行ったつもりで master が開く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語集の全件を、名前・種別・値のタイプの 3 列と案件の印つきで一覧表示し、語と種別・値のタイプの切替で絞り込む。
 * @graph-connects useListTerms [calls] 用語集の全件取得
 */
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import type { TermListOutput, TermPjMark, TermSummary } from '@cortex/domain-spec-console';
import { Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SpecDiffBadge, specDiffTextClass } from '../components/spec/SpecDiffState.js';
import { ErrorState } from '../components/spec/fallback-states.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { toElementId, useListTerms } from '../hooks/queries/spec-graph-element-term-queries.js';
import { TERMS_ROUTE, TERM_DETAIL_ROUTE } from './route-paths.js';
import { pjLinkSearch } from './pj-link.js';
import { pjTextOf } from './pj-search.js';
import { termFieldApplies } from './term-field-applicability.js';

/** ローディング時に出すスケルトンの行数。 @graph-connects none */
const SKELETON_ROWS = 6;

/**
 * 種別 / 値のタイプの訳語キーを引く。未設定なら `null`。
 *
 * 契約が返すのは `class` / `code` のような原語である。訳語の表を i18n に置き、画面はキーを
 * 組むだけにする。原語をそのまま出すと、日本語で読んでいる人に `indicator` が並ぶ。
 *
 * @param prefix 訳語の表（`termSection` / `termRepresentation`）
 * @param value 契約が返した原語。未設定なら null
 * @returns 訳語のキー。未設定なら null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別・値のタイプの原語を i18n の訳語キーへ写す。
 * @graph-connects none
 */
export function termEnumKey(prefix: string, value: string | null): string | null {
  return value === null || value === '' ? null : `${prefix}.${value}`;
}

/**
 * 種別 / 値のタイプを行に出す文字へ直す。未設定なら「未設定」。
 *
 * @param translate i18n の翻訳関数
 * @param prefix 訳語の表（`termSection` / `termRepresentation`）
 * @param value 契約が返した原語。未設定なら null
 * @returns 行に出す文字
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別・値のタイプを行に出す文字へ直し、未設定なら「未設定」と言う。
 * @graph-connects termEnumKey [calls] 原語から訳語キーへの写像
 */
export function enumLabel(
  translate: (key: string) => string,
  prefix: string,
  value: string | null,
): string {
  const key = termEnumKey(prefix, value);
  return key === null ? translate('termDetail.unset') : translate(key);
}

/**
 * 用語 1 件が絞り込みの語に一致するか。名前・id・種別・値のタイプのいずれかで拾う。
 *
 * 属するクラスでは拾わない。一覧に出していない欄で拾うと、行のどこにも打った語が見えないのに
 * 残る行ができ、なぜ残ったかが読めない。
 *
 * **行に見えている訳語でも引けるようにする。** 契約が返すのは原語（`property` / `indicator`）
 * だが、行に出ているのは訳語（「プロパティ」「真偽」）である。原語だけで絞ると、画面に見えて
 * いる語をそのまま打った人が 0 件に落ちる。原語も残すのは、英語で読んでいる人と、原語を知って
 * いる書き手の両方が引けるようにするためである。
 *
 * 名前は表示中の言語のものだけを見る。読取は 1 locale しか返さないので、他言語の名前は
 * そもそも手元に無い。
 *
 * @param term 用語 1 件
 * @param keyword 絞り込みの語（小文字化済み）
 * @param display 行に出ている種別と値のタイプの訳語
 * @returns 一致すれば true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語 1 件が絞り込みの語（名前・id・種別・値のタイプの原語と訳語）に一致するかを判定する。
 * @graph-connects none
 */
export function matchesTermKeyword(
  term: TermSummary,
  keyword: string,
  display: TermDisplayLabels,
): boolean {
  if (keyword === '') return true;
  const needle = keyword.toLowerCase();
  const candidates = [
    term.label,
    String(term.termId),
    term.section ?? '',
    term.representation ?? '',
    display.section,
    display.representation,
  ];
  return candidates.some((candidate) => candidate.toLowerCase().includes(needle));
}

/** 行に出ている種別と値のタイプの訳語。 @graph-connects none */
export interface TermDisplayLabels {
  section: string;
  representation: string;
}

/** 用語集の 1 行。案件モードでは、案件の中での変わり方を持つ。 @graph-connects none */
export interface TermListRow {
  /** 用語 1 件 */
  term: TermSummary;
  /** 案件の中での変わり方。案件を選んでいない・変わっていなければ null */
  mark: TermPjMark | null;
}

/**
 * 取得した用語集を、画面に並べる順の行へ組む。
 *
 * 案件モードでは、案件で足した用語と消した用語を一覧の上に集める（足した → 消した の順）。
 * 変えた用語は元の位置のまま印だけ付ける。案件を選んでいなければ、読取が印を返しても付けない
 * — 案件を選んでいない画面に札が出ると、どの案件の印かが読めない。
 *
 * @param data 取得した用語集
 * @param pjMode 案件を選んでいるか
 * @returns 並べる順の行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の行を、案件モードなら案件で足した・消した用語を上に集め、印つきで並べる。
 * @graph-connects none
 */
export function buildTermListRows(
  data: Pick<TermListOutput, 'items' | 'removed'>,
  pjMode: boolean,
): TermListRow[] {
  if (!pjMode) return data.items.map((term) => ({ term, mark: null }));
  const markOf = (term: TermSummary): TermListRow => ({ term, mark: term.pjMark ?? null });
  const created = data.items.filter((term) => term.pjMark === 'created').map(markOf);
  const removed = (data.removed ?? []).map((term) => ({ term, mark: 'deleted' as const }));
  const rest = data.items.filter((term) => term.pjMark !== 'created').map(markOf);
  return [...created, ...removed, ...rest];
}

/** 種別・値のタイプの切替で「未設定」を表す値。原語は空文字にならないので重ならない。 @graph-connects none */
export const TERM_FILTER_UNSET = '';

/**
 * 種別・値のタイプの切替の状態。`null` は「すべて」、`TERM_FILTER_UNSET` は「未設定」。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の種別と値のタイプの切替で選んでいる値を表す。
 * @graph-connects none
 */
export interface TermShapeFilter {
  section: string | null;
  representation: string | null;
}

/**
 * 用語 1 件が種別・値のタイプの切替に一致するか。「すべて」の段は見ない。
 *
 * @param term 用語 1 件
 * @param filter 切替で選んでいる値
 * @returns 一致すれば true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語 1 件が、種別と値のタイプの切替（未設定を含む）の両方に一致するかを判定する。
 * @graph-connects TERM_FILTER_UNSET [references] 未設定を表す値
 */
export function matchesTermShapeFilter(term: TermSummary, filter: TermShapeFilter): boolean {
  const hit = (selected: string | null, value: string | null) =>
    selected === null || selected === (value ?? TERM_FILTER_UNSET);
  return hit(filter.section, term.section) && hit(filter.representation, term.representation);
}

/**
 * 切替の選択肢 1 つ。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の切替の選択肢 1 つ（原語・訳語）を表す。
 * @graph-connects none
 */
export interface TermFilterOption {
  value: string;
  label: string;
}

/**
 * 行に現れる値から、切替の選択肢を組む。訳語を表示言語の順に並べ、未設定は最後に置く。
 *
 * **並べる順は表示言語に追従する。** 言語を固定すると、英語やベトナム語で読んでいる人には訳語が
 * その言語の辞書順に並ばない（業務詳細の業務ルールの並びと同じく `i18n.resolvedLanguage` を渡す）。
 *
 * **値の一覧を画面に写さない。** 種別・値のタイプの取りうる値は仕様グラフの側が持つ。画面に
 * 並びを写すと、向こうで値が増えたときに黙って選べなくなる。行に現れない値は選んでも 0 件なので
 * 出さない。
 *
 * @param values 行の値（未設定は null）
 * @param labelOf 原語から訳語へ
 * @param unsetLabel 「未設定」の文言
 * @param locale 表示言語（`i18n.resolvedLanguage`）。並べる順を決める
 * @returns 切替の選択肢
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の行に現れる種別・値のタイプから、表示言語の訳語順で未設定を最後にした切替の選択肢を組む。
 * @graph-connects TERM_FILTER_UNSET [references] 未設定を表す値
 */
export function termFilterOptions(
  values: readonly (string | null)[],
  labelOf: (value: string) => string,
  unsetLabel: string,
  locale: string | undefined,
): TermFilterOption[] {
  const present = new Set(values.map((value) => value ?? TERM_FILTER_UNSET));
  const named = [...present]
    .filter((value) => value !== TERM_FILTER_UNSET)
    .map((value) => ({ value, label: labelOf(value) }))
    .sort((left, right) => left.label.localeCompare(right.label, locale));
  return present.has(TERM_FILTER_UNSET)
    ? [...named, { value: TERM_FILTER_UNSET, label: unsetLabel }]
    : named;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語集を見出し・絞り込みの語・種別と値のタイプの切替・名前と種別と値のタイプの一覧で描く。
 * @graph-connects useListTerms [calls] 用語集の全件取得
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 * @graph-connects buildTermListRows [calls] 案件の印つきの行の並び
 * @graph-connects matchesTermKeyword [calls] 絞り込みの語の一致
 * @graph-connects matchesTermShapeFilter [calls] 種別と値のタイプの切替の一致
 * @graph-connects termFilterOptions [calls] 切替の選択肢
 * @graph-connects enumLabel [calls] 種別・値のタイプの訳語
 * @graph-connects toElementId [calls] 案件番号を数へ読む
 * @graph-connects TermFilterRow [calls] 切替の 1 段
 * @graph-connects TermListBody [calls] 一覧の本体
 */
export function TermsPage() {
  const { t, i18n } = useTranslation();
  const pj = pjTextOf(useSearch({ from: TERMS_ROUTE }).pj);
  const pjNumber = toElementId(pj);
  const { data, isLoading, error } = useListTerms(pjNumber);
  const [keyword, setKeyword] = useState('');
  const [shape, setShape] = useState<TermShapeFilter>({ section: null, representation: null });

  const displayOf = (term: TermSummary): TermDisplayLabels => ({
    section: enumLabel(t, 'termSection', term.section),
    representation: enumLabel(t, 'termRepresentation', term.representation),
  });
  const allRows = data === undefined ? [] : buildTermListRows(data, pjNumber !== undefined);
  const rows = allRows.filter(
    (row) =>
      matchesTermShapeFilter(row.term, shape) &&
      matchesTermKeyword(row.term, keyword.trim().toLowerCase(), displayOf(row.term)),
  );
  const unset = t('termDetail.unset');
  const sectionOptions = termFilterOptions(
    allRows.map((row) => row.term.section),
    (value) => enumLabel(t, 'termSection', value),
    unset,
    i18n.resolvedLanguage,
  );
  const representationOptions = termFilterOptions(
    allRows.map((row) => row.term.representation),
    (value) => enumLabel(t, 'termRepresentation', value),
    unset,
    i18n.resolvedLanguage,
  );

  /*
   * 語の入力欄の見出しは字で出さず、読み上げ用の名前（aria-label）にだけ残す。用途は薄い字の例で読める。
   */
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('terms.title')}</h1>
      </header>

      <Card padding="md">
        <div className="flex flex-col gap-2">
          <TermFilterRow
            label={t('termDetail.sectionTitle')}
            name="section"
            value={shape.section}
            options={sectionOptions}
            onSelect={(section) => {
              setShape((prev) => ({ ...prev, section }));
            }}
          />
          <TermFilterRow
            label={t('termDetail.typeTitle')}
            name="representation"
            value={shape.representation}
            options={representationOptions}
            onSelect={(representation) => {
              setShape((prev) => ({ ...prev, representation }));
            }}
          />
          <Input
            fullWidth
            aria-label={t('terms.filterLabel')}
            placeholder={t('terms.filterPlaceholder')}
            value={keyword}
            onChange={(event) => {
              setKeyword(event.target.value);
            }}
          />
        </div>
      </Card>

      <TermListBody
        rows={rows}
        isLoading={isLoading}
        error={error}
        pj={pjNumber === undefined ? undefined : pj}
      />
    </div>
  );
}

/** `TermFilterRow` の props。 @graph-connects none */
interface TermFilterRowProps {
  /** 段の見出し。読み上げる段の名前にも使う。 */
  label: string;
  /** 段の識別子（`data-term-filter`）。 */
  name: string;
  /** 選んでいる値。`null` は「すべて」。 */
  value: string | null;
  options: readonly TermFilterOption[];
  onSelect: (value: string | null) => void;
}

/**
 * 切替の 1 段（見出し ・ すべて ＋ 選択肢のボタン）。画面一覧の絞り込みの切替と同じ見た目にする
 * — 同じ操作が画面ごとに違う形で出ると、読み手は画面を移るたびに使い方を確かめ直す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の種別・値のタイプの切替 1 段を、見出しとすべて＋選択肢のボタンの並びで描き、選んだ値を返す。
 * @graph-connects none
 */
function TermFilterRow({ label, name, value, options, onSelect }: TermFilterRowProps) {
  const { t } = useTranslation();
  const buttons: readonly { key: string; value: string | null; label: string }[] = [
    { key: 'all', value: null, label: t('terms.filterAll') },
    ...options.map((option) => ({ ...option, key: option.value === '' ? 'unset' : option.value })),
  ];
  return (
    <div className="flex items-center gap-3" data-term-filter={name}>
      <span className="w-20 flex-none text-xs font-bold text-[var(--color-text-muted)]">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
        {buttons.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={value === option.value}
            data-filter-option={option.key}
            onClick={() => {
              onSelect(option.value);
            }}
            className={
              value === option.value
                ? 'rounded-sm border border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] px-2 py-0.5 text-xs text-[var(--color-bg-base)]'
                : 'rounded-sm border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
            }
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** `TermListBody` の props。 @graph-connects none */
interface TermListBodyProps {
  rows: readonly TermListRow[];
  isLoading: boolean;
  error: unknown;
  /** 重ねている案件番号。行のリンクへ引き継ぐ。案件を選んでいなければ undefined */
  pj?: string;
}

/** 表のセルの共通 class。 @graph-connects none */
const CELL_CLASS = 'border-b border-[var(--color-border)] px-3 py-2 text-left align-top text-sm';

/** 表の見出しのセルの class。 @graph-connects none */
const HEAD_CLASS = `${CELL_CLASS} text-xs font-bold text-[var(--color-text-muted)]`;

/**
 * 用語一覧の本体（失敗 / 読み込み中 / 空 / 表）の出し分け。空の時は「条件に一致する用語が
 * ありません」の 1 文だけを出す（「該当するものがありません」を重ねない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語一覧を失敗・読み込み中・空・表の 4 状態で出し分ける。
 * @graph-connects TermTable [calls] 用語集の表
 * @graph-connects ErrorState [calls] 失敗
 */
function TermListBody({ rows, isLoading, error, pj }: TermListBodyProps) {
  const { t } = useTranslation();

  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('common.loading')}>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <Skeleton key={index} variant="rectangular" height="2.5rem" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p role="status" className="text-sm text-[var(--color-text-muted)]">
        {t('terms.empty')}
      </p>
    );
  }

  return <TermTable rows={rows} pj={pj} />;
}

/**
 * 用語集の表。列は 名前 ／ 種別 ／ 値のタイプ で、案件モードでも増やさない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集の行を、名前・種別・値のタイプの 3 列の表で描く。
 * @graph-connects TermRow [calls] 用語 1 行
 */
function TermTable({ rows, pj }: Pick<TermListBodyProps, 'rows' | 'pj'>) {
  const { t } = useTranslation();

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th scope="col" className={HEAD_CLASS}>
            {t('terms.columnName')}
          </th>
          <th scope="col" className={`${HEAD_CLASS} w-32`}>
            {t('termDetail.sectionTitle')}
          </th>
          <th scope="col" className={`${HEAD_CLASS} w-32`}>
            {t('termDetail.typeTitle')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <TermRow key={`${row.mark ?? ''}:${String(row.term.termId)}`} row={row} pj={pj} />
        ))}
      </tbody>
    </table>
  );
}

/** `TermRow` の props。 @graph-connects none */
interface TermRowProps {
  row: TermListRow;
  /** 重ねている案件番号。詳細へのリンクへ引き継ぐ。案件を選んでいなければ undefined */
  pj?: string;
}

/**
 * 用語集の 1 行。名前・種別・値のタイプを出し、案件モードでは名前の頭に案件の札を置く。未翻訳の
 * 印は名前の title に回す。種別がプロパティでない用語は値のタイプを書けないので、そのセルを空ける
 * （「未設定」と書くと、書き漏れに見える）。
 *
 * **行全体の遷移先は名前のリンク 1 本のまま**にする（2026-09-14 に決めた。一覧の行の中に用語の
 * リンクを重ねない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語 1 件の案件の札・名前・種別の札・値のタイプを 1 行で描き、用語詳細へ繋ぐ。
 * @graph-connects TERM_DETAIL_ROUTE [references] 用語詳細
 * @graph-connects SpecDiffBadge [calls] 案件の印の札
 * @graph-connects specDiffTextClass [calls] 消した用語の取り消し線
 * @graph-connects pjLinkSearch [calls] 案件を詳細へ引き継ぐ検索条件
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 * @graph-connects TermSectionBadge [calls] 種別の札
 * @graph-connects enumLabel [calls] 値のタイプの訳語
 * @graph-connects termFieldApplies [calls] 値のタイプを書ける用語かの判定
 */
function TermRow({ row, pj }: TermRowProps) {
  const { t } = useTranslation();
  const { term, mark } = row;
  const title = term.label !== '' && term.untranslated ? t('spec.untranslated') : undefined;
  const representationKey = termEnumKey('termRepresentation', term.representation);
  /*
   * **消した用語の名前には下線を付けず、打ち消しだけを付ける。** 下線（`underline`）と打ち消し
   * （`line-through`）はどちらも `text-decoration-line` を決めるので、同じ要素に並べると
   * className の並びではなくスタイルシート上の順番で片方が勝つ。実際に下線が勝ち、消した用語が
   * 取り消し線の無いふつうのリンクに見えていた。文字色も同じで、リンクの色と打ち消しの薄い色を
   * 並べずにどちらか一方だけを渡す。
   */
  const nameClass =
    mark === 'deleted'
      ? specDiffTextClass('deleted')
      : 'text-[var(--color-accent-primary)] underline';

  return (
    <tr data-id={term.termId} data-mark={mark ?? undefined}>
      <td className={CELL_CLASS}>
        <span className="flex flex-wrap items-center gap-2">
          {pj !== undefined && mark !== null && <SpecDiffBadge state={mark} />}
          <Link
            to={TERM_DETAIL_ROUTE}
            params={{ termId: String(term.termId) }}
            {...pjLinkSearch(pj)}
            {...(title === undefined ? {} : { title })}
            className={`font-bold ${nameClass}`}
          >
            {displayLabel(term.label, t, term.untranslated)}
          </Link>
        </span>
      </td>
      <td className={CELL_CLASS} data-column="section">
        <TermSectionBadge section={term.section} />
      </td>
      <td className={CELL_CLASS} data-column="representation">
        {!termFieldApplies('representation', term) ? null : representationKey === null ? (
          <span className="text-xs text-[var(--color-text-muted)]">{t('termDetail.unset')}</span>
        ) : (
          t(representationKey)
        )}
      </td>
    </tr>
  );
}

/**
 * 種別ごとの札の見た目。クラス ＝ 塗り、プロパティ ＝ 枠、値 ＝ 破線の枠。
 * @graph-connects none
 */
const TERM_SECTION_BADGE_CLASS: Readonly<Record<string, string>> = {
  class:
    'border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-bg-base)]',
  property: 'border-[var(--color-text-primary)] text-[var(--color-text-primary)]',
  value: 'border-dashed border-[var(--color-text-primary)] text-[var(--color-text-primary)]',
};

/** 知らない種別の札。枠だけにする。 @graph-connects none */
const TERM_SECTION_BADGE_FALLBACK =
  'border-[var(--color-border)] text-[var(--color-text-secondary)]';

/** `TermSectionBadge` の props。 @graph-connects none */
export interface TermSectionBadgeProps {
  /** 種別の原語。未設定なら null */
  section: string | null;
}

/**
 * 種別の札。クラス ＝ 塗り、プロパティ ＝ 枠、値 ＝ 破線の枠で、未設定は「未設定」の文字。
 *
 * 文字（「種別: クラス」）で並べると、一覧を縦に流し読みしたときに種別の違いが目に入らない。
 * 形を 3 つに分けておけば、振り分けの漏れ（未設定）と種別の偏りが一覧のまま読める。
 *
 * @param props 種別の原語
 * @returns 種別の札
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別を、クラス・プロパティ・値で形の違う札として描き、未設定は「未設定」と出す。
 * @graph-connects termEnumKey [calls] 種別の訳語キー
 */
export function TermSectionBadge({ section }: TermSectionBadgeProps) {
  const { t } = useTranslation();
  const key = termEnumKey('termSection', section);
  if (key === null || section === null) {
    return <span className="text-xs text-[var(--color-text-muted)]">{t('termDetail.unset')}</span>;
  }
  return (
    <span
      data-section={section}
      className={`inline-block border px-1.5 text-[10px] font-bold leading-4 tracking-wide ${TERM_SECTION_BADGE_CLASS[section] ?? TERM_SECTION_BADGE_FALLBACK}`}
    >
      {t(key)}
    </span>
  );
}
